import { useState, useCallback, useEffect, useRef } from "react";
import type {
  TokenEntry,
  TokenGroup,
  TokenAction,
  TokenTab,
  TokenPositions,
  TokenOrigin,
  TokenHistorySnapshot,
} from "@/components/timetable-import/typesV2";
import {
  extractTokenSignature,
  invalidateSignatureCache,
  calculateStructuralSimilarity,
  calculateAdaptiveSimilarity,
  applyBatchEdit,
  splitConfirmedFromCompound,
} from "@/utils/batchEdit";
import { getOriginKey } from "@/utils/tokenOrigin";
import { isIgnoredToken } from "@/utils/ignoredTokens";
import { compactKey, sameSurface } from "@/utils/tokenPatterns";
import { shouldAutoConfirmVeryHighConfidence } from "@/utils/tokenConfidence";

interface TokenStoreConfig {
  threshold: number;
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>;
  grouped: boolean;
  currentTab: string;
  workflowStep: string;
}

interface TokenStoreState {
  matched: Record<string, TokenEntry>;
  unmatched: Record<string, TokenEntry>;
  wrong: Record<string, TokenEntry>;
  ignored: Record<string, TokenEntry>;
  groups: TokenGroup[];
  history: TokenAction[];
  config: TokenStoreConfig;
  splits: Record<string, string[]>; // Track split tokens: originalToken → [parts]
}

function captureSnapshot(prev: TokenStoreState): TokenHistorySnapshot {
  return {
    matched: prev.matched,
    unmatched: prev.unmatched,
    wrong: prev.wrong,
    ignored: prev.ignored,
    splits: prev.splits,
  };
}

function pushHistory(
  prev: TokenStoreState,
  action: TokenAction
): TokenAction[] {
  return [
    ...prev.history.slice(-19),
    { ...action, snapshot: captureSnapshot(prev) },
  ];
}

function fileSplitParent(
  ignored: Record<string, TokenEntry>,
  token: string,
  entry: TokenEntry,
  parts: string[],
  autoSplit: boolean
) {
  const splitInto = parts.filter((part) => part);
  if (splitInto.length < 2) return;
  ignored[token] = {
    ...entry,
    token,
    field: "ignore",
    ignored: true,
    wrong: false,
    confirmed: true,
    confirmedField: "ignore",
    is_split: true,
    auto_split: autoSplit,
    split_into: splitInto,
  };
}

function asIgnored(source: TokenEntry, token: string): TokenEntry {
  const splitInto = source.split_into?.filter((part) => part);
  const keepSplit = Boolean(
    source.is_split || (splitInto && splitInto.length >= 2)
  );
  return {
    ...source,
    token,
    field: "ignore",
    ignored: true,
    wrong: false,
    confirmed: true,
    confirmedField: "ignore",
    is_split: keepSplit,
    auto_split: keepSplit ? Boolean(source.auto_split) : false,
    split_into: keepSplit ? splitInto : undefined,
  };
}

function snapshotBucket(
  snapshot: TokenHistorySnapshot,
  token: string
): "unmatched" | "wrong" | "matched" | "ignored" | null {
  if (snapshot.unmatched[token]) return "unmatched";
  if (snapshot.wrong[token]) return "wrong";
  if (snapshot.ignored[token]) return "ignored";
  if (snapshot.matched[token]) return "matched";
  return null;
}

export type TokenUndoResult = {
  tokens: string[];
  bucket: "unmatched" | "wrong" | "matched" | "ignored";
  action: string;
};

function getSignature(token: string) {
  return extractTokenSignature(token);
}

function calcSimilarity(
  sig1: ReturnType<typeof extractTokenSignature>,
  sig2: ReturnType<typeof extractTokenSignature>,
  t1: string,
  t2: string
): number {
  if (sig1.category === sig2.category && sig1.category !== "unknown") {
    const base = 0.5;
    const lenSim =
      1 - Math.abs(t1.length - t2.length) / Math.max(t1.length, t2.length);
    return base + lenSim * 0.5;
  }
  const common = Math.min(sig1.segmentCount, sig2.segmentCount);
  let matchCount = 0;
  const s1 = t1.split(/[-.\/:_#+]+/).filter((s) => s);
  const s2 = t2.split(/[-.\/:_#+]+/).filter((s) => s);
  for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
    if (s1[i]!.toLowerCase() === s2[i]!.toLowerCase()) matchCount++;
  }
  return common > 0 ? matchCount / common : 0;
}

function groupAvgSim(
  group: string[],
  sigs: Map<string, ReturnType<typeof extractTokenSignature>>
): number {
  if (group.length < 2) return 1;
  let total = 0;
  let count = 0;
  for (let i = 0; i < group.length; i++) {
    for (let j = i + 1; j < group.length; j++) {
      const sig1 = sigs.get(group[i]!);
      const sig2 = sigs.get(group[j]!);
      if (sig1 && sig2) {
        total += calcSimilarity(sig1, sig2, group[i]!, group[j]!);
        count++;
      }
    }
  }
  return count > 0 ? total / count : 0;
}

const TEACHER_IDENTITY_FIELDS = new Set([
  "teacher_code",
  "teacher_name",
  "first_name",
  "family_name",
]);

function mergePositions(
  a?: TokenPositions | null,
  b?: TokenPositions | null
): TokenPositions {
  const left = a || { cols: [], rows: [], count: 0 };
  const right = b || { cols: [], rows: [], count: 0 };
  return {
    cols: [...new Set([...(left.cols || []), ...(right.cols || [])])],
    rows: [...new Set([...(left.rows || []), ...(right.rows || [])])],
    count: (left.count || 0) + (right.count || 0),
  };
}

function suggestField(
  token: string,
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>
): { field: string; confidence: number } | null {
  for (const p of orphanPatterns) {
    if (TEACHER_IDENTITY_FIELDS.has(p.field)) continue;
    try {
      const re = new RegExp(p.regex, "i");
      if (p.match_mode === "full" && re.test(token))
        return { field: p.field, confidence: 0.9 };
      if (p.match_mode === "prefix" && re.test(token))
        return { field: p.field, confidence: 0.7 };
      if (p.match_mode === "search" && re.test(token))
        return { field: p.field, confidence: 0.6 };
    } catch {
      // ignore invalid regex
    }
  }
  const sig = getSignature(token);
  const fieldMap: Record<string, string> = {
    timeslot: "time_slot",
    time: "time_slot",
    room: "room",
    class: "class_name",
  };
  const field = fieldMap[sig.category];
  if (field) return { field, confidence: 0.5 };
  return null;
}

export type SavedTokenStatus = {
  field?: string;
  confirmed?: boolean;
  canonical_subject_id?: string | null;
};

const BLOCKED_SAVED_FIELDS = new Set(["", "split"]);

type SplitBuckets = {
  matched: Record<string, TokenEntry>;
  ignored: Record<string, TokenEntry>;
  wrong: Record<string, TokenEntry>;
  unmatched: Record<string, TokenEntry>;
};

function collapseIgnored(
  ignored: Record<string, TokenEntry>
): Record<string, TokenEntry> {
  const out: Record<string, TokenEntry> = {};
  const names: string[] = [];
  for (const [token, entry] of Object.entries(ignored)) {
    const text = (entry?.token || token).trim();
    if (!text) {
      if (!(token in out)) out[token] = entry;
      continue;
    }
    if (isIgnoredToken(text, names)) continue;
    names.push(text);
    out[text] = entry?.token === text ? entry : { ...entry, token: text };
  }
  return out;
}

function hasReusableField(entry: TokenEntry | undefined): entry is TokenEntry {
  if (!entry) return false;
  if (entry.ignored || entry.field === "ignore") return true;
  return Boolean(entry.field || entry.canonical_subject_id);
}

function findReusableEntry(
  state: SplitBuckets,
  part: string,
  parentToken: string
): TokenEntry | null {
  const needle = part.trim().toLowerCase();
  if (!needle) return null;
  const entries = [
    ...Object.values(state.matched),
    ...Object.values(state.ignored),
  ].filter((entry) => {
    return (
      hasReusableField(entry) &&
      entry.token !== parentToken &&
      entry.token.trim().toLowerCase() !== parentToken.trim().toLowerCase()
    );
  });
  const direct = state.matched[part] || state.ignored[part];
  if (hasReusableField(direct) && direct.token !== parentToken) return direct;
  const lower = entries.find(
    (entry) => entry.token.trim().toLowerCase() === needle
  );
  if (lower) return lower;
  const compact = compactKey(part);
  if (!compact) return null;
  const compactHits = entries.filter(
    (entry) => compactKey(entry.token) === compact
  );
  const fields = new Set(
    compactHits.map((entry) =>
      entry.ignored || entry.field === "ignore" ? "ignore" : entry.field
    )
  );
  if (fields.size !== 1) return null;
  return compactHits[0] || null;
}

function savedField(status: SavedTokenStatus | undefined): string | null {
  const field = status?.field || "";
  if (!field || BLOCKED_SAVED_FIELDS.has(field)) return null;
  if (field === "ignore" || status?.confirmed) return field;
  return null;
}

function findSavedStatus(
  part: string,
  saved: Record<string, SavedTokenStatus> | undefined
): { field: string; canonical_subject_id: string | null } | null {
  if (!saved) return null;
  const direct = saved[part] || saved[part.toLowerCase()];
  const directField = savedField(direct);
  if (directField)
    return {
      field: directField,
      canonical_subject_id: direct?.canonical_subject_id ?? null,
    };
  const needle = part.trim().toLowerCase();
  if (!needle) return null;
  for (const [key, status] of Object.entries(saved)) {
    if (key.trim().toLowerCase() !== needle) continue;
    const field = savedField(status);
    if (field)
      return {
        field,
        canonical_subject_id: status?.canonical_subject_id ?? null,
      };
  }
  const compact = compactKey(part);
  if (!compact) return null;
  const hits: Array<{ field: string; canonical_subject_id: string | null }> =
    [];
  for (const [key, status] of Object.entries(saved)) {
    if (compactKey(key) !== compact) continue;
    const field = savedField(status);
    if (!field) continue;
    hits.push({
      field,
      canonical_subject_id: status?.canonical_subject_id ?? null,
    });
  }
  const fields = new Set(hits.map((hit) => hit.field));
  if (fields.size !== 1) return null;
  return hits[0] || null;
}

type PartPlan =
  | { kind: "reuse"; entry: TokenEntry }
  | { kind: "saved"; field: string; canonical_subject_id: string | null }
  | { kind: "fresh"; field: string | null; confidence: number };

function planSplitPart(
  part: string,
  parentToken: string,
  state: SplitBuckets,
  ignoredNames: string[],
  saved: Record<string, SavedTokenStatus> | undefined,
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>
): PartPlan {
  const reused = findReusableEntry(state, part, parentToken);
  if (reused) return { kind: "reuse", entry: reused };
  if (isIgnoredToken(part, ignoredNames))
    return { kind: "saved", field: "ignore", canonical_subject_id: null };
  const known = findSavedStatus(part, saved);
  if (known)
    return {
      kind: "saved",
      field: known.field,
      canonical_subject_id: known.canonical_subject_id,
    };
  const suggestion = suggestField(part, orphanPatterns);
  return {
    kind: "fresh",
    field: suggestion ? suggestion.field : null,
    confidence: suggestion ? suggestion.confidence : 0,
  };
}

function compoundOrigin(
  parentEntry: TokenEntry,
  parentToken: string
): TokenOrigin | null {
  const parentOrigin = parentEntry.origin;
  if (!parentOrigin) return null;
  return {
    ...parentOrigin,
    structural: {
      is_part_of_compound: true,
      compound_parent: parentToken,
      related_subject: parentOrigin.structural?.related_subject,
      related_subject_confirmed:
        parentOrigin.structural?.related_subject_confirmed ?? false,
      appears_in_columns: parentOrigin.structural?.appears_in_columns ?? [],
      appears_in_rows: parentOrigin.structural?.appears_in_rows ?? [],
      co_occurs_with: parentOrigin.structural?.co_occurs_with ?? {},
    },
  };
}

function placeSplitEntry(maps: SplitBuckets, entry: TokenEntry) {
  delete maps.unmatched[entry.token];
  delete maps.wrong[entry.token];
  delete maps.matched[entry.token];
  delete maps.ignored[entry.token];
  if (entry.ignored || entry.field === "ignore") {
    maps.ignored[entry.token] = {
      ...entry,
      ignored: true,
      field: "ignore",
      confirmed: true,
      confirmedField: "ignore",
    };
    return;
  }
  if (entry.field) {
    maps.matched[entry.token] = entry;
    return;
  }
  maps.unmatched[entry.token] = entry;
}

function unresolvedSubject(
  part: string,
  plan: PartPlan,
  saved: Record<string, SavedTokenStatus> | undefined
): boolean {
  if (plan.kind === "fresh") return false;
  if (plan.kind === "saved")
    return plan.field === "subject" && !plan.canonical_subject_id;
  if (
    plan.entry.ignored ||
    plan.entry.field === "ignore" ||
    plan.entry.field !== "subject"
  )
    return false;
  if (plan.entry.canonical_subject_id) return false;
  const known = findSavedStatus(part, saved);
  return !(known?.field === "subject" && known.canonical_subject_id);
}

function writeSplitPart(
  maps: SplitBuckets,
  part: string,
  parentToken: string,
  parentEntry: TokenEntry,
  splitId: string,
  ignoredNames: string[],
  saved: Record<string, SavedTokenStatus> | undefined,
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>
): "kept" | "matched" | "unmatched" {
  const plan = planSplitPart(
    part,
    parentToken,
    maps,
    ignoredNames,
    saved,
    orphanPatterns
  );
  if (plan.kind === "reuse") {
    const known = findSavedStatus(part, saved);
    const linkedId =
      plan.entry.canonical_subject_id ||
      (known?.field === "subject" ? known.canonical_subject_id : null);
    const kept =
      plan.entry.field === "subject" && linkedId
        ? {
            ...plan.entry,
            canonical_subject_id: linkedId,
            has_canonical_link: true,
            confirmed: true,
            confirmedField: "subject",
          }
        : plan.entry;
    const positions = mergePositions(kept.positions, parentEntry.positions);
    placeSplitEntry(maps, { ...kept, positions });
    if (kept.token !== part) {
      placeSplitEntry(maps, {
        ...kept,
        token: part,
        positions: parentEntry.positions,
        splitFrom: parentToken,
        splitId,
      });
    }
    return "kept";
  }
  const field = plan.field;
  const subjectId = plan.kind === "saved" ? plan.canonical_subject_id : null;
  const subjectUnlinked = field === "subject" && !subjectId;
  const confirmed = plan.kind === "saved" && !subjectUnlinked;
  const existing =
    maps.matched[part] ||
    maps.ignored[part] ||
    maps.unmatched[part] ||
    maps.wrong[part];
  const entry: TokenEntry = {
    token: part,
    field,
    confidence: plan.kind === "saved" ? 0.95 : plan.confidence,
    wrong: false,
    positions: mergePositions(existing?.positions, parentEntry.positions),
    splitFrom: parentToken,
    splitId,
    ignored: field === "ignore",
    confirmed: field === "ignore" ? true : confirmed,
    confirmedField: field === "ignore" ? "ignore" : confirmed ? field : null,
    canonical_subject_id: subjectId,
    has_canonical_link: field === "subject" ? Boolean(subjectId) : null,
    source: plan.kind === "saved" ? "token_history" : parentEntry.source,
    pattern: parentEntry.pattern,
    origin: compoundOrigin(parentEntry, parentToken),
    originConfirmed: {},
  };
  placeSplitEntry(maps, entry);
  if (plan.kind === "saved") return "kept";
  return field ? "matched" : "unmatched";
}

function buildInitialState(
  initialMatched: Record<
    string,
    {
      field: string;
      confidence: number;
      positions?: TokenPositions;
      source?: string | null;
      pattern?: string | null;
      origin?: TokenOrigin | null;
      has_canonical_link?: boolean | null;
      teacher_exists?: boolean | null;
      is_tentative?: boolean;
      auto_confirmed?: boolean;
      wasPreviouslyConfirmed?: boolean;
      is_split?: boolean;
      auto_split?: boolean;
      split_into?: string[];
    }
  >,
  initialUnmatched: Record<
    string,
    { positions?: TokenPositions; origin?: TokenOrigin | null }
  >,
  orphanPatterns: Array<{ regex: string; field: string; match_mode: string }>,
  tokenPositions: Record<string, TokenPositions>,
  splitTracking: Record<string, string>
): TokenStoreState {
  const confirmedStorage = loadConfirmedFromStorage();

  // Parse origin-specific confirmations from sessionStorage (keys with "::" separator)
  const originConfirmations: Record<string, Record<string, boolean>> = {};
  for (const [key, value] of Object.entries(confirmedStorage)) {
    const sepIdx = key.indexOf("::");
    if (sepIdx !== -1 && value.confirmed) {
      const token = key.substring(0, sepIdx);
      const originKey = key.substring(sepIdx + 2);
      if (!originConfirmations[token]) {
        originConfirmations[token] = {};
      }
      originConfirmations[token][originKey] = true;
    }
  }

  const matched: Record<string, TokenEntry> = {};
  const ignored: Record<string, TokenEntry> = {};
  let autoConfirmedCount = 0;
  let storageConfirmedCount = 0;
  for (const [token, info] of Object.entries(initialMatched)) {
    const stored = confirmedStorage[token];
    // Token is confirmed if:
    // 1. It's confirmed in sessionStorage with matching field, OR
    // 2. It has auto_confirmed flag (from template/knownTokens), OR
    // 3. It has wasPreviouslyConfirmed flag (from template/knownTokens), OR
    // 4. Room/class/time at very high confidence (not a heuristic source)
    const highConfidence = shouldAutoConfirmVeryHighConfidence({
      field: info.field,
      confidence: info.confidence,
      source: info.source,
    });
    const isConfirmed =
      (stored?.confirmed === true && stored?.confirmedField === info.field) ||
      info.auto_confirmed === true ||
      info.wasPreviouslyConfirmed === true ||
      highConfidence;
    if (isConfirmed && info.field !== "ignore") {
      if (
        info.auto_confirmed ||
        info.wasPreviouslyConfirmed ||
        highConfidence
      ) {
        autoConfirmedCount++;
      } else {
        storageConfirmedCount++;
      }
    }
    const entry: TokenEntry = {
      token,
      field: info.field,
      confidence: info.confidence,
      wrong: false,
      positions: info.positions || { cols: [], rows: [], count: 0 },
      splitFrom: splitTracking[token] || null,
      splitId: null,
      ignored: info.field === "ignore",
      confirmed: info.field === "ignore" ? true : isConfirmed,
      confirmedField:
        info.field === "ignore" ? "ignore" : isConfirmed ? info.field : null,
      source: info.source || null,
      pattern: info.pattern || null,
      origin: info.origin || null,
      originConfirmed: originConfirmations[token] || {},
      has_canonical_link: info.has_canonical_link ?? null,
      teacher_exists: info.teacher_exists ?? null,
      is_tentative: info.is_tentative ?? false,
      is_split: info.is_split ?? false,
      auto_split: info.auto_split ?? false,
      split_into: info.split_into ?? undefined,
    };
    if (info.field === "ignore") ignored[token] = entry;
    else matched[token] = entry;
  }
  if (import.meta.env.DEV) {
    console.log("[useTokenStore] buildInitialState matched:", {
      total: Object.keys(matched).length,
      confirmed: autoConfirmedCount + storageConfirmedCount,
      autoConfirmed: autoConfirmedCount,
      storageConfirmed: storageConfirmedCount,
    });
  }
  const unmatched: Record<string, TokenEntry> = {};
  const matchedByLower = new Map([
    ...Object.keys(matched).map((t) => [t.toLowerCase(), t] as const),
    ...Object.keys(ignored).map((t) => [t.toLowerCase(), t] as const),
  ]);
  for (const [token, info] of Object.entries(initialUnmatched)) {
    if (matched[token] || ignored[token]) continue;
    const positions = info.positions ||
      tokenPositions[token] || { cols: [], rows: [], count: 0 };
    if (!token.trim()) {
      ignored[token] = {
        token,
        field: "ignore",
        confidence: 1,
        wrong: false,
        positions,
        splitFrom: splitTracking[token] || null,
        splitId: null,
        ignored: true,
        confirmed: true,
        confirmedField: "ignore",
        source: null,
        pattern: null,
        origin: info.origin || null,
        originConfirmed: originConfirmations[token] || {},
      };
      continue;
    }
    const dupKey = matchedByLower.get(token.toLowerCase());
    if (dupKey) {
      const src = matched[dupKey] || ignored[dupKey];
      if (src?.field === "ignore" || ignored[dupKey]) continue;
      if (src) {
        matched[token] = {
          ...src,
          token,
          positions: src.positions?.count ? src.positions : positions,
        };
      }
      continue;
    }
    unmatched[token] = {
      token,
      field: null,
      confidence: 0,
      wrong: false,
      positions,
      splitFrom: splitTracking[token] || null,
      splitId: null,
      ignored: false,
      confirmed: false,
      confirmedField: null,
      source: null,
      pattern: null,
      origin: info.origin || null,
      originConfirmed: originConfirmations[token] || {},
    };
  }
  return {
    matched,
    unmatched,
    wrong: {},
    ignored: collapseIgnored(ignored),
    groups: [],
    history: [],
    splits: {}, // Track split tokens: originalToken → [parts]
    config: {
      threshold: 0.5,
      orphanPatterns,
      grouped: false,
      currentTab: "matched",
      workflowStep: Object.keys(unmatched).length > 0 ? "unmatched" : "save",
    },
  };
}

interface ConfirmedStorageEntry {
  confirmed: boolean;
  confirmedField: string | null;
  originKey?: string | null;
}

type ConfirmedStorage = Record<string, ConfirmedStorageEntry>;

// Phase 8: sessionStorage usage notes
// sessionStorage is used for INTRA-SESSION persistence (Undo functionality within same import).
// CROSS-SESSION persistence (restarts, new imports) relies on backend template's
// wasPreviouslyConfirmed flag via API response, NOT sessionStorage.
//
// Important: sessionStorage is cleared when:
// - Tab/browser closes
// - User navigates away from the import page
// - New bulk import session starts
//
// The backend template (linked via batch.auto_selected_template_id) provides
// the authoritative confirmation status for subsequent imports.

export const getConfirmedStorageKey = (sessionId?: string) => {
  // Use sessionId for more stable key within the same import session
  // Falls back to pathname if sessionId not available
  return sessionId
    ? `token_confirmed_session_${sessionId}`
    : `token_confirmed_${window.location.pathname}`;
};

const loadConfirmedFromStorage = (sessionId?: string): ConfirmedStorage => {
  try {
    const data = sessionStorage.getItem(getConfirmedStorageKey(sessionId));
    if (data) {
      return JSON.parse(data);
    }
  } catch {
    // ignore
  }
  return {};
};

const saveConfirmedToStorage = (state: TokenStoreState, sessionId?: string) => {
  try {
    const storage: ConfirmedStorage = {};
    const saveEntry = (entry: TokenEntry) => {
      if (entry.confirmed) {
        storage[entry.token] = {
          confirmed: true,
          confirmedField: entry.confirmedField,
          originKey: entry.origin ? getOriginKey(entry.origin) : null,
        };
      }
      // Save origin-specific confirmations with :: separator
      if (entry.originConfirmed) {
        for (const [key, confirmed] of Object.entries(entry.originConfirmed)) {
          if (confirmed) {
            storage[`${entry.token}::${key}`] = {
              confirmed: true,
              confirmedField: entry.confirmedField,
            };
          }
        }
      }
    };
    for (const entry of Object.values(state.matched)) saveEntry(entry);
    for (const entry of Object.values(state.wrong)) saveEntry(entry);
    for (const entry of Object.values(state.ignored)) saveEntry(entry);
    sessionStorage.setItem(
      getConfirmedStorageKey(sessionId),
      JSON.stringify(storage)
    );
  } catch {
    // ignore
  }
};

export function useTokenStore(
  initialMatched: Record<
    string,
    {
      field: string;
      confidence: number;
      positions?: TokenPositions;
      source?: string | null;
      pattern?: string | null;
      origin?: TokenOrigin | null;
      has_canonical_link?: boolean | null;
      teacher_exists?: boolean | null;
      is_tentative?: boolean;
      auto_confirmed?: boolean;
      wasPreviouslyConfirmed?: boolean;
      is_split?: boolean;
      auto_split?: boolean;
      split_into?: string[];
    }
  > = {},
  initialUnmatched: Record<
    string,
    { positions?: TokenPositions; origin?: TokenOrigin | null }
  > = {},
  orphanPatterns: Array<{
    regex: string;
    field: string;
    match_mode: string;
  }> = [],
  tokenPositions: Record<string, TokenPositions> = {},
  splitTracking: Record<string, string> = {}
) {
  const [state, setState] = useState<TokenStoreState>(() =>
    buildInitialState(
      initialMatched,
      initialUnmatched,
      orphanPatterns,
      tokenPositions,
      splitTracking
    )
  );

  const stateRef = useRef(state);
  const autoSplitRef = useRef(new Set<string>());
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const [selectedTokens, setSelectedTokens] = useState<string[]>([]);

  const prevKeysRef = useRef<string>("");
  const prevValuesRef = useRef<string>("");
  useEffect(() => {
    const key = `${Object.keys(initialMatched).join(",")}-${Object.keys(initialUnmatched).join(",")}`;
    // Also check property changes (e.g., has_canonical_link, teacher_exists) by stringifying values
    const values = JSON.stringify({
      matched: Object.entries(initialMatched).map(
        ([k, v]) =>
          `${k}:${v.field}:${v.has_canonical_link}:${v.teacher_exists}:${v.auto_confirmed}:${v.wasPreviouslyConfirmed}`
      ),
      unmatched: Object.keys(initialUnmatched),
    });
    if (key !== prevKeysRef.current || values !== prevValuesRef.current) {
      prevKeysRef.current = key;
      prevValuesRef.current = values;
      setState((prev) => {
        const built = buildInitialState(
          initialMatched,
          initialUnmatched,
          orphanPatterns,
          tokenPositions,
          splitTracking
        );
        const ignored = { ...built.ignored };
        const unmatched = { ...built.unmatched };
        const matched = { ...built.matched };
        const splits = { ...built.splits, ...prev.splits };
        for (const token of Object.keys(prev.splits)) {
          delete unmatched[token];
          delete matched[token];
        }
        for (const parts of Object.values(prev.splits)) {
          for (const part of parts) {
            if (prev.ignored[part]) ignored[part] = prev.ignored[part];
            else if (prev.matched[part]) matched[part] = prev.matched[part];
            else if (prev.unmatched[part] && !matched[part] && !ignored[part])
              unmatched[part] = prev.unmatched[part];
          }
        }
        for (const [token, entry] of Object.entries(prev.ignored)) {
          if (!token.trim() || isIgnoredToken(token, Object.keys(ignored)))
            continue;
          ignored[token] = entry;
          delete unmatched[token];
          delete matched[token];
        }
        const collapsed = collapseIgnored(ignored);
        for (const token of Object.keys(ignored)) {
          if (!collapsed[token]) delete ignored[token];
        }
        Object.assign(ignored, collapsed);
        const ignoredNames = Object.keys(ignored);
        for (const token of Object.keys(unmatched)) {
          if (!isIgnoredToken(token, ignoredNames)) continue;
          delete unmatched[token];
        }
        for (const token of Object.keys(matched)) {
          if (
            matched[token]?.field === "ignore" ||
            isIgnoredToken(token, ignoredNames)
          )
            delete matched[token];
        }
        const wrong = { ...built.wrong };
        const serverTokens = [
          ...Object.keys(matched),
          ...Object.keys(unmatched),
        ];
        const suffixOnly = (short: string) =>
          serverTokens.some(
            (full) =>
              full.length > short.length &&
              full.toLowerCase().endsWith(short.toLowerCase()) &&
              !sameSurface(full, short)
          );
        for (const [token, entry] of Object.entries(prev.matched)) {
          if (
            !entry?.confirmed ||
            !entry.field ||
            entry.field === "ignore" ||
            entry.field === "split"
          )
            continue;
          if (
            matched[token] ||
            ignored[token] ||
            isIgnoredToken(token, ignoredNames)
          )
            continue;
          const aligned = serverTokens.find((full) => sameSurface(full, token));
          if (aligned) {
            matched[aligned] = entry;
            delete unmatched[aligned];
            delete wrong[aligned];
            continue;
          }
          if (entry.field === "room" && suffixOnly(token)) {
            matched[token] = entry;
            delete unmatched[token];
            delete wrong[token];
          }
        }
        return {
          ...built,
          ignored,
          unmatched,
          matched,
          wrong,
          splits,
          history: prev.history,
        };
      });
    }
  }, [
    initialMatched,
    initialUnmatched,
    orphanPatterns,
    tokenPositions,
    splitTracking,
  ]);

  // Persist confirmed tokens to sessionStorage
  useEffect(() => {
    saveConfirmedToStorage(state);
  }, [state.matched, state.wrong, state.ignored]);

  const assign = useCallback((token: string, field: string) => {
    setState((prev) => {
      const entry = prev.unmatched[token] || prev.wrong[token];
      if (entry) {
        const sig = getSignature(token);
        const similarTokens = Object.keys({
          ...prev.matched,
          ...prev.unmatched,
        }).filter((t) => {
          if (t === token) return false;
          const otherSig = getSignature(t);
          return (
            sig.category === otherSig.category && sig.category !== "unknown"
          );
        });
        const hasConflict = similarTokens.some((t) => {
          const otherEntry = prev.matched[t];
          return otherEntry && otherEntry.field && otherEntry.field !== field;
        });

        const newEntry = {
          ...entry,
          field,
          confirmed: !hasConflict,
          confirmedField: !hasConflict ? field : null,
        };
        const newMatched = { ...prev.matched, [token]: newEntry };
        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        delete newUnmatched[token];
        delete newWrong[token];

        // Debug: log split token assignment
        if (entry.splitFrom) {
          console.log("[useTokenStore] assign split token:", {
            token,
            field,
            splitFrom: entry.splitFrom,
            confirmed: !hasConflict,
            hasConflict,
          });
        }

        const newHistory = pushHistory(prev, {
          action: "assign",
          token,
          field,
        });
        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          history: newHistory,
        };
      }
      if (prev.matched[token]) {
        const existing = prev.matched[token]!;
        const sig = getSignature(token);
        const similarTokens = Object.keys({
          ...prev.matched,
          ...prev.unmatched,
        }).filter((t) => {
          if (t === token) return false;
          const otherSig = getSignature(t);
          return (
            sig.category === otherSig.category && sig.category !== "unknown"
          );
        });
        const hasConflict = similarTokens.some((t) => {
          const otherEntry = prev.matched[t];
          return otherEntry && otherEntry.field && otherEntry.field !== field;
        });
        const newMatched = { ...prev.matched };
        newMatched[token] = {
          ...existing,
          field,
          confirmed: !hasConflict,
          confirmedField: !hasConflict ? field : null,
        };
        const newHistory = pushHistory(prev, {
          action: "reassign",
          token,
          field,
          prevField: existing.field ?? undefined,
        });
        return { ...prev, matched: newMatched, history: newHistory };
      }
      return prev;
    });
  }, []);

  const assignAndConfirm = useCallback(
    (
      token: string,
      field: string,
      extra?: { canonical_subject_id?: string | null }
    ) => {
      setState((prev) => {
        const existing =
          prev.unmatched[token] ||
          prev.wrong[token] ||
          prev.matched[token] ||
          prev.ignored[token];
        const entry: TokenEntry = existing
          ? { ...existing }
          : {
              token,
              field: null,
              confidence: 1,
              wrong: false,
              positions: { cols: [], rows: [], count: 0 },
              splitFrom: null,
              splitId: null,
              ignored: false,
              confirmed: false,
              confirmedField: null,
            };
        const canonicalSubjectId =
          extra?.canonical_subject_id ?? entry.canonical_subject_id ?? null;
        const newEntry: TokenEntry = {
          ...entry,
          field,
          confirmed: true,
          confirmedField: field,
          canonical_subject_id: canonicalSubjectId,
          has_canonical_link: canonicalSubjectId
            ? true
            : entry.has_canonical_link,
          overrideIgnore: true,
          originConfirmed: entry.origin
            ? {
                ...(entry.originConfirmed || {}),
                [getOriginKey(entry.origin)]: true,
              }
            : entry.originConfirmed,
        };
        const newMatched = { ...prev.matched, [token]: newEntry };
        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        const newIgnored = { ...prev.ignored };
        delete newUnmatched[token];
        delete newWrong[token];
        delete newIgnored[token];
        const action = prev.matched[token]
          ? "reassignAndConfirm"
          : "assignAndConfirm";
        const newHistory = pushHistory(prev, {
          action,
          token,
          field,
          prevField: prev.matched[token]?.field ?? undefined,
        });
        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          ignored: newIgnored,
          history: newHistory,
        };
      });
    },
    []
  );

  const assignAndConfirmMany = useCallback(
    (
      tokens: string[],
      field: string,
      extra?: { canonical_subject_id?: string | null }
    ) => {
      if (!tokens.length) return;
      setState((prev) => {
        const newMatched = { ...prev.matched };
        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        const newIgnored = { ...prev.ignored };
        const applied: string[] = [];
        for (const token of tokens) {
          const existing =
            newUnmatched[token] ||
            newWrong[token] ||
            newMatched[token] ||
            newIgnored[token];
          const entry: TokenEntry = existing
            ? { ...existing }
            : {
                token,
                field: null,
                confidence: 1,
                wrong: false,
                positions: { cols: [], rows: [], count: 0 },
                splitFrom: null,
                splitId: null,
                ignored: false,
                confirmed: false,
                confirmedField: null,
              };
          const canonicalSubjectId =
            extra?.canonical_subject_id ?? entry.canonical_subject_id ?? null;
          newMatched[token] = {
            ...entry,
            field,
            confirmed: true,
            confirmedField: field,
            canonical_subject_id: canonicalSubjectId,
            has_canonical_link: canonicalSubjectId
              ? true
              : entry.has_canonical_link,
            overrideIgnore: true,
            originConfirmed: entry.origin
              ? {
                  ...(entry.originConfirmed || {}),
                  [getOriginKey(entry.origin)]: true,
                }
              : entry.originConfirmed,
          };
          delete newUnmatched[token];
          delete newWrong[token];
          delete newIgnored[token];
          applied.push(token);
        }
        if (!applied.length) return prev;
        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          ignored: newIgnored,
          history: pushHistory(prev, {
            action: "assignAndConfirm",
            tokens: applied,
            field,
          }),
        };
      });
    },
    []
  );

  const unassign = useCallback((token: string) => {
    setState((prev) => {
      if (prev.matched[token]) {
        const entry = {
          ...prev.matched[token],
          field: null,
          confirmed: false,
          confirmedField: null,
        };
        const newMatched = { ...prev.matched };
        const newUnmatched = { ...prev.unmatched, [token]: entry };
        delete newMatched[token];
        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          history: pushHistory(prev, { action: "unassign", token }),
        };
      }
      return prev;
    });
  }, []);

  const markWrong = useCallback((token: string) => {
    setState((prev) => {
      if (prev.matched[token]) {
        const entry = {
          ...prev.matched[token],
          wrong: true,
          confirmed: false,
          confirmedField: null,
        };
        const newMatched = { ...prev.matched };
        const newWrong = { ...prev.wrong, [token]: entry };
        delete newMatched[token];
        return {
          ...prev,
          matched: newMatched,
          wrong: newWrong,
          history: pushHistory(prev, { action: "markWrong", token }),
        };
      }
      return prev;
    });
  }, []);

  const unmarkWrong = useCallback((token: string) => {
    setState((prev) => {
      if (prev.wrong[token]) {
        const entry = { ...prev.wrong[token], wrong: false };
        const newWrong = { ...prev.wrong };
        const newMatched = { ...prev.matched, [token]: entry };
        delete newWrong[token];
        return {
          ...prev,
          wrong: newWrong,
          matched: newMatched,
          history: pushHistory(prev, { action: "unmarkWrong", token }),
        };
      }
      return prev;
    });
  }, []);

  const ignore = useCallback((token: string) => {
    setState((prev) => {
      const source = prev.unmatched[token] ||
        prev.wrong[token] ||
        prev.matched[token] ||
        prev.ignored[token] || {
          token,
          field: null,
          confidence: 1,
          wrong: false,
          positions: { cols: [], rows: [], count: 0 },
          splitFrom: null,
          splitId: null,
          ignored: false,
          confirmed: false,
          confirmedField: null,
        };
      const entry = asIgnored(source, token);
      const newUnmatched = { ...prev.unmatched };
      const newWrong = { ...prev.wrong };
      const newMatched = { ...prev.matched };
      const newIgnored = { ...prev.ignored, [token]: entry };
      const newSplits = { ...prev.splits };
      delete newUnmatched[token];
      delete newWrong[token];
      delete newMatched[token];
      delete newSplits[token];
      return {
        ...prev,
        unmatched: newUnmatched,
        wrong: newWrong,
        matched: newMatched,
        ignored: newIgnored,
        splits: newSplits,
        history: pushHistory(prev, { action: "ignore", token }),
      };
    });
  }, []);

  const batchIgnore = useCallback((tokens: string[]) => {
    setState((prev) => {
      const newUnmatched = { ...prev.unmatched };
      const newMatched = { ...prev.matched };
      const newWrong = { ...prev.wrong };
      const newIgnored = { ...prev.ignored };
      const ignoredTokens: string[] = [];

      for (const token of tokens) {
        const source = prev.unmatched[token] ||
          prev.wrong[token] ||
          prev.matched[token] ||
          prev.ignored[token] || {
            token,
            field: null,
            confidence: 1,
            wrong: false,
            positions: { cols: [], rows: [], count: 0 },
            splitFrom: null,
            splitId: null,
            ignored: false,
            confirmed: false,
            confirmedField: null,
          };
        newIgnored[token] = asIgnored(source, token);
        delete newUnmatched[token];
        delete newWrong[token];
        delete newMatched[token];
        ignoredTokens.push(token);
      }

      if (!ignoredTokens.length) return prev;
      const newSplits = { ...prev.splits };
      for (const token of ignoredTokens) delete newSplits[token];
      return {
        ...prev,
        unmatched: newUnmatched,
        matched: newMatched,
        wrong: newWrong,
        ignored: newIgnored,
        splits: newSplits,
        history: pushHistory(prev, { action: "ignore", tokens: ignoredTokens }),
      };
    });
  }, []);

  const unignore = useCallback((token: string) => {
    setState((prev) => {
      if (prev.ignored[token]) {
        const entry = {
          ...prev.ignored[token],
          ignored: false,
          field: null,
          confirmed: false,
          confirmedField: null,
        };
        const newIgnored = { ...prev.ignored };
        const newUnmatched = { ...prev.unmatched, [token]: entry };
        delete newIgnored[token];
        return {
          ...prev,
          ignored: newIgnored,
          unmatched: newUnmatched,
          history: pushHistory(prev, { action: "unignore", token }),
        };
      }
      if (prev.matched[token]?.field === "ignore") {
        const entry = {
          ...prev.matched[token],
          field: null,
          ignored: false,
          confirmed: false,
          confirmedField: null,
        };
        const newMatched = { ...prev.matched };
        delete newMatched[token];
        return {
          ...prev,
          matched: newMatched,
          unmatched: { ...prev.unmatched, [token]: entry },
          history: pushHistory(prev, { action: "unignore", token }),
        };
      }
      return prev;
    });
  }, []);

  const split = useCallback(
    (
      token: string,
      parts: string[],
      ignoredNames: string[] = [],
      savedTokens?: Record<string, SavedTokenStatus>
    ): {
      matchedParts: string[];
      unmatchedParts: string[];
      keptParts: string[];
      unlinkedSubjects: string[];
    } => {
      const entry =
        stateRef.current.unmatched[token] ||
        stateRef.current.wrong[token] ||
        stateRef.current.matched[token];
      if (!entry)
        return {
          matchedParts: [],
          unmatchedParts: [],
          keptParts: [],
          unlinkedSubjects: [],
        };
      parts = parts.filter((part) => part);
      if (parts.length < 2)
        return {
          matchedParts: [],
          unmatchedParts: [],
          keptParts: [],
          unlinkedSubjects: [],
        };

      const matchedParts: string[] = [];
      const unmatchedParts: string[] = [];
      const keptParts: string[] = [];
      const unlinkedSubjects: string[] = [];
      const partsToInvalidate: string[] = [];

      for (const part of parts) {
        if (!part) continue;
        const plan = planSplitPart(
          part,
          token,
          stateRef.current,
          ignoredNames,
          savedTokens,
          stateRef.current.config.orphanPatterns
        );
        if (plan.kind === "reuse" || plan.kind === "saved") {
          keptParts.push(part);
          if (unresolvedSubject(part, plan, savedTokens))
            unlinkedSubjects.push(part);
          continue;
        }
        if (plan.field) matchedParts.push(part);
        else unmatchedParts.push(part);
        partsToInvalidate.push(part);
      }

      setState((prev) => {
        const prevEntry =
          prev.unmatched[token] || prev.wrong[token] || prev.matched[token];
        if (!prevEntry) return prev;
        const splitId = `split_${Date.now()}`;
        const maps: SplitBuckets = {
          unmatched: { ...prev.unmatched },
          wrong: { ...prev.wrong },
          matched: { ...prev.matched },
          ignored: { ...prev.ignored },
        };
        delete maps.unmatched[token];
        delete maps.wrong[token];
        delete maps.matched[token];
        fileSplitParent(maps.ignored, token, prevEntry, parts, false);
        for (const part of parts) {
          if (!part) continue;
          writeSplitPart(
            maps,
            part,
            token,
            prevEntry,
            splitId,
            ignoredNames,
            savedTokens,
            prev.config.orphanPatterns
          );
        }
        const newSplits = { ...prev.splits, [token]: parts };
        return {
          ...prev,
          unmatched: maps.unmatched,
          wrong: maps.wrong,
          matched: maps.matched,
          ignored: maps.ignored,
          splits: newSplits,
          history: pushHistory(prev, {
            action: "split",
            token,
            parts,
            splitId,
          }),
        };
      });

      for (const part of partsToInvalidate) {
        invalidateSignatureCache(part);
      }

      return { matchedParts, unmatchedParts, keptParts, unlinkedSubjects };
    },
    []
  );

  /**
   * Batch split: split primary token and apply same toggle pattern to all selected tokens.
   * Returns { newTokens, skipped } for status reporting.
   */
  const batchSplit = useCallback(
    (
      primaryToken: string,
      parts: string[],
      splitIndices: number[],
      otherTokens: string[],
      ignoredNames: string[] = [],
      savedTokens?: Record<string, SavedTokenStatus>
    ): { newTokens: string[]; skipped: number; unlinkedSubjects: string[] } => {
      const planOf = (part: string, parent: string) =>
        planSplitPart(
          part,
          parent,
          stateRef.current,
          ignoredNames,
          savedTokens,
          stateRef.current.config.orphanPatterns
        );
      const needsAsk = (part: string, parent: string) =>
        planOf(part, parent).kind === "fresh";
      const unlinkedSubjects: string[] = [];
      const rememberUnlinked = (part: string, parent: string) => {
        if (
          unresolvedSubject(part, planOf(part, parent), savedTokens) &&
          !unlinkedSubjects.includes(part)
        )
          unlinkedSubjects.push(part);
      };
      const newTokens: string[] = parts.filter(
        (part) => part && needsAsk(part, primaryToken)
      );
      for (const part of parts) rememberUnlinked(part, primaryToken);
      let skipped = 0;
      const allPartsToInvalidate: string[] = [];
      const allSplits: Record<string, string[]> = {};
      const planned: Array<{
        token: string;
        parts: string[];
        entry: TokenEntry;
        splitId: string;
      }> = [];
      const tokensToRemove: string[] = [];

      for (const otherToken of otherTokens) {
        let otherSep = " ";
        if (!/\s/.test(otherToken)) {
          const sepCounts: Record<string, number> = {};
          const seps = otherToken.match(/[-_./\\]/g);
          if (seps) {
            for (const s of seps) {
              sepCounts[s] = (sepCounts[s] || 0) + 1;
            }
            let maxCount = 0;
            for (const s in sepCounts) {
              if (sepCounts[s]! > maxCount) {
                maxCount = sepCounts[s]!;
                otherSep = s;
              }
            }
          }
        }

        const otherEscapedSep = otherSep.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const otherWords = otherToken
          .split(new RegExp(otherEscapedSep + "+"))
          .filter((w) => w);

        const otherParts: string[] = [];
        let current = otherWords[0] || "";
        for (let i = 0; i < otherWords.length - 1; i++) {
          if (splitIndices.includes(i)) {
            otherParts.push(current);
            current = otherWords[i + 1] || "";
          } else {
            current += otherSep + (otherWords[i + 1] || "");
          }
        }
        otherParts.push(current);

        if (otherParts.length <= 1) {
          skipped++;
          continue;
        }

        const entry =
          stateRef.current.unmatched[otherToken] ||
          stateRef.current.wrong[otherToken] ||
          stateRef.current.matched[otherToken];
        if (!entry) continue;

        const splitId = `split_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const concreteParts = otherParts.filter((part) => part);
        planned.push({
          token: otherToken,
          parts: concreteParts,
          entry,
          splitId,
        });
        tokensToRemove.push(otherToken);
        allSplits[otherToken] = otherParts;
        for (const part of concreteParts) {
          rememberUnlinked(part, otherToken);
          if (needsAsk(part, otherToken) && !newTokens.includes(part)) {
            newTokens.push(part);
            allPartsToInvalidate.push(part);
          }
        }
      }

      if (tokensToRemove.length > 0) {
        setState((prev) => {
          const maps: SplitBuckets = {
            unmatched: { ...prev.unmatched },
            wrong: { ...prev.wrong },
            matched: { ...prev.matched },
            ignored: { ...prev.ignored },
          };
          for (const token of tokensToRemove) {
            delete maps.unmatched[token];
            delete maps.wrong[token];
            delete maps.matched[token];
          }
          for (const item of planned) {
            fileSplitParent(
              maps.ignored,
              item.token,
              item.entry,
              item.parts,
              false
            );
            for (const part of item.parts) {
              writeSplitPart(
                maps,
                part,
                item.token,
                item.entry,
                item.splitId,
                ignoredNames,
                savedTokens,
                prev.config.orphanPatterns
              );
            }
          }
          return {
            ...prev,
            unmatched: maps.unmatched,
            wrong: maps.wrong,
            matched: maps.matched,
            ignored: maps.ignored,
            splits: { ...prev.splits, ...allSplits },
            history: pushHistory(prev, {
              action: "split",
              tokens: tokensToRemove,
            }),
          };
        });
      }

      for (const part of allPartsToInvalidate) {
        invalidateSignatureCache(part);
      }

      return { newTokens, skipped, unlinkedSubjects };
    },
    []
  );

  const applyAssignedSplits = useCallback(
    (
      items: {
        token: string;
        parts: string[];
        fields: string[];
        subjectIds: Array<string | null>;
      }[],
      ignoredNames: string[] = []
    ) => {
      if (!items.length) return;
      setState((prev) => {
        const unmatched = { ...prev.unmatched };
        const wrong = { ...prev.wrong };
        const matched = { ...prev.matched };
        const ignored = { ...prev.ignored };
        const splits = { ...prev.splits };
        const removed: string[] = [];
        const catalog = [...ignoredNames, ...Object.keys(ignored)];
        const dropSurface = (part: string) => {
          for (const key of Object.keys(unmatched)) {
            if (isIgnoredToken(key, [part]) || isIgnoredToken(part, [key]))
              delete unmatched[key];
          }
          for (const key of Object.keys(wrong)) {
            if (isIgnoredToken(key, [part]) || isIgnoredToken(part, [key]))
              delete wrong[key];
          }
        };
        for (const item of items) {
          const parent = unmatched[item.token] || wrong[item.token];
          if (
            !parent ||
            splits[item.token] ||
            item.parts.length < 2 ||
            item.parts.length !== item.fields.length
          )
            continue;
          if (
            !item.fields.some((field) => field) &&
            !item.parts.some((part) => isIgnoredToken(part, catalog))
          )
            continue;
          delete unmatched[item.token];
          delete wrong[item.token];
          removed.push(item.token);
          autoSplitRef.current.add(item.token);
          splits[item.token] = item.parts;
          fileSplitParent(ignored, item.token, parent, item.parts, true);
          item.parts.forEach((part, index) => {
            const field = isIgnoredToken(part, catalog)
              ? "ignore"
              : item.fields[index] || "";
            if (field === "ignore") dropSurface(part);
            if (!field) {
              const kept = matched[part] || ignored[part];
              if (kept) {
                const next = {
                  ...kept,
                  positions: mergePositions(kept.positions, parent.positions),
                };
                if (matched[part]) matched[part] = next;
                else ignored[part] = next;
                return;
              }
              const prior = unmatched[part] || wrong[part];
              delete wrong[part];
              unmatched[part] = {
                token: part,
                field: null,
                confidence: prior?.confidence ?? 0,
                wrong: false,
                positions: mergePositions(prior?.positions, parent.positions),
                splitFrom: item.token,
                splitId: `auto_${item.token}`,
                ignored: false,
                confirmed: false,
                confirmedField: null,
                canonical_subject_id: null,
                has_canonical_link: null,
                source: prior?.source ?? null,
                pattern: null,
                origin: compoundOrigin(parent, item.token),
                originConfirmed: prior?.originConfirmed ?? {},
              };
              return;
            }
            const subjectId = item.subjectIds[index] ?? null;
            const existing =
              matched[part] || ignored[part] || unmatched[part] || wrong[part];
            const entry: TokenEntry = {
              token: part,
              field,
              confidence: 0.95,
              wrong: false,
              positions: mergePositions(existing?.positions, parent.positions),
              splitFrom: item.token,
              splitId: `auto_${item.token}`,
              ignored: field === "ignore",
              confirmed: true,
              confirmedField: field,
              canonical_subject_id: field === "subject" ? subjectId : null,
              has_canonical_link:
                field === "subject" ? Boolean(subjectId) : null,
              source: "pattern",
              pattern: null,
              origin: compoundOrigin(parent, item.token),
              originConfirmed: {},
            };
            delete unmatched[part];
            delete wrong[part];
            delete matched[part];
            delete ignored[part];
            if (field === "ignore") ignored[part] = entry;
            else matched[part] = entry;
          });
        }
        if (!removed.length) return prev;
        return {
          ...prev,
          unmatched,
          wrong,
          matched,
          ignored,
          splits,
          history: pushHistory(prev, { action: "split", tokens: removed }),
        };
      });
    },
    []
  );

  const splitMany = useCallback(
    (
      items: { token: string; parts: string[] }[],
      ignoredNames: string[] = [],
      savedTokens?: Record<string, SavedTokenStatus>
    ): { newTokens: string[]; skipped: number; unlinkedSubjects: string[] } => {
      const newTokens: string[] = [];
      const unlinkedSubjects: string[] = [];
      let skipped = 0;
      const allPartsToInvalidate: string[] = [];
      const allSplits: Record<string, string[]> = {};
      const planned: Array<{
        token: string;
        parts: string[];
        entry: TokenEntry;
        splitId: string;
      }> = [];
      const tokensToRemove: string[] = [];

      for (const item of items) {
        const parts = item.parts.filter((part) => part);
        if (parts.length < 2) {
          skipped++;
          continue;
        }
        const entry =
          stateRef.current.unmatched[item.token] ||
          stateRef.current.wrong[item.token] ||
          stateRef.current.matched[item.token];
        if (!entry) {
          skipped++;
          continue;
        }
        const splitId = `split_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        planned.push({ token: item.token, parts, entry, splitId });
        tokensToRemove.push(item.token);
        allSplits[item.token] = parts;
        for (const part of parts) {
          const plan = planSplitPart(
            part,
            item.token,
            stateRef.current,
            ignoredNames,
            savedTokens,
            stateRef.current.config.orphanPatterns
          );
          if (
            unresolvedSubject(part, plan, savedTokens) &&
            !unlinkedSubjects.includes(part)
          )
            unlinkedSubjects.push(part);
          if (plan.kind === "fresh" && !newTokens.includes(part)) {
            newTokens.push(part);
            allPartsToInvalidate.push(part);
          }
        }
      }

      if (tokensToRemove.length > 0) {
        setState((prev) => {
          const maps: SplitBuckets = {
            unmatched: { ...prev.unmatched },
            wrong: { ...prev.wrong },
            matched: { ...prev.matched },
            ignored: { ...prev.ignored },
          };
          for (const token of tokensToRemove) {
            delete maps.unmatched[token];
            delete maps.wrong[token];
            delete maps.matched[token];
          }
          for (const item of planned) {
            fileSplitParent(
              maps.ignored,
              item.token,
              item.entry,
              item.parts,
              false
            );
            for (const part of item.parts) {
              writeSplitPart(
                maps,
                part,
                item.token,
                item.entry,
                item.splitId,
                ignoredNames,
                savedTokens,
                prev.config.orphanPatterns
              );
            }
          }
          return {
            ...prev,
            unmatched: maps.unmatched,
            wrong: maps.wrong,
            matched: maps.matched,
            ignored: maps.ignored,
            splits: { ...prev.splits, ...allSplits },
            history: pushHistory(prev, {
              action: "split",
              tokens: tokensToRemove,
            }),
          };
        });
      }

      for (const part of allPartsToInvalidate) {
        invalidateSignatureCache(part);
      }

      return { newTokens, skipped, unlinkedSubjects };
    },
    []
  );

  const merge = useCallback((sourceTokens: string[], targetToken: string) => {
    setState((prev) => {
      const target =
        prev.unmatched[targetToken] ||
        prev.matched[targetToken] ||
        prev.wrong[targetToken];
      if (!target) return prev;
      const combined = targetToken + " " + sourceTokens.join(" ");
      const newTarget = {
        ...target,
        token: combined,
        confirmed: false,
        confirmedField: null,
      };
      const newUnmatched = { ...prev.unmatched };
      const newMatched = { ...prev.matched };
      const newWrong = { ...prev.wrong };
      for (const st of sourceTokens) {
        delete newUnmatched[st];
        delete newMatched[st];
        delete newWrong[st];
      }
      if (prev.unmatched[targetToken]) {
        delete newUnmatched[targetToken];
        newUnmatched[combined] = newTarget;
      }
      if (prev.matched[targetToken]) {
        delete newMatched[targetToken];
        newMatched[combined] = newTarget;
      }
      if (prev.wrong[targetToken]) {
        delete newWrong[targetToken];
        newWrong[combined] = newTarget;
      }
      return {
        ...prev,
        unmatched: newUnmatched,
        matched: newMatched,
        wrong: newWrong,
        history: pushHistory(prev, {
          action: "merge",
          sources: sourceTokens,
          target: combined,
        }),
      };
    });
  }, []);

  /**
   * Rename a token from oldToken to newToken in whichever map it resides.
   * Also updates splitFrom references.
   * If newToken already exists (case-insensitive), merges positions and skips duplicate.
   */
  const renameToken = useCallback((oldToken: string, newToken: string) => {
    if (oldToken === newToken) return;
    setState((prev) => {
      const entry =
        prev.unmatched[oldToken] ||
        prev.wrong[oldToken] ||
        prev.matched[oldToken] ||
        prev.ignored[oldToken];
      if (!entry) return prev;

      const source = prev.unmatched[oldToken]
        ? "unmatched"
        : prev.wrong[oldToken]
          ? "wrong"
          : prev.matched[oldToken]
            ? "matched"
            : "ignored";

      invalidateSignatureCache(oldToken);
      invalidateSignatureCache(newToken);

      // Update splitFrom references
      const updateSplitFrom = (map: Record<string, TokenEntry>) => {
        const result: Record<string, TokenEntry> = {};
        for (const key of Object.keys(map)) {
          const val = map[key];
          if (val) {
            result[key] =
              val.splitFrom === oldToken
                ? { ...val, splitFrom: newToken }
                : val;
          }
        }
        return result;
      };

      let newUnmatched = updateSplitFrom(prev.unmatched);
      let newWrong = updateSplitFrom(prev.wrong);
      let newMatched = updateSplitFrom(prev.matched);
      let newIgnored = updateSplitFrom(prev.ignored);

      // Check for duplicate (case-insensitive)
      const allTokens = [
        ...Object.keys(newMatched),
        ...Object.keys(newUnmatched),
        ...Object.keys(newWrong),
        ...Object.keys(newIgnored),
      ];
      const existingToken = allTokens.find(
        (t) => t.toLowerCase() === newToken.toLowerCase() && t !== oldToken
      );

      if (existingToken) {
        // Remove old token
        delete newUnmatched[oldToken];
        delete newWrong[oldToken];
        delete newMatched[oldToken];
        delete newIgnored[oldToken];
        return {
          ...prev,
          unmatched: newUnmatched,
          wrong: newWrong,
          matched: newMatched,
          ignored: newIgnored,
          history: pushHistory(prev, {
            action: "rename",
            token: oldToken,
            newToken,
          }),
        };
      } else {
        // No duplicate: proceed with rename
        const newEntry = { ...entry, token: newToken };
        if (source === "unmatched") {
          delete newUnmatched[oldToken];
          newUnmatched[newToken] = newEntry;
        } else if (source === "wrong") {
          delete newWrong[oldToken];
          newWrong[newToken] = newEntry;
        } else if (source === "matched") {
          delete newMatched[oldToken];
          newMatched[newToken] = newEntry;
        } else {
          delete newIgnored[oldToken];
          newIgnored[newToken] = newEntry;
        }
        return {
          ...prev,
          unmatched: newUnmatched,
          wrong: newWrong,
          matched: newMatched,
          ignored: newIgnored,
          history: pushHistory(prev, {
            action: "rename",
            token: oldToken,
            newToken,
          }),
        };
      }
    });
  }, []);

  const renameAndConfirm = useCallback(
    (
      oldToken: string,
      newToken: string,
      field?: string | null,
      doConfirm?: boolean
    ) => {
      if (oldToken === newToken) return;
      setState((prev) => {
        const entry =
          prev.unmatched[oldToken] ||
          prev.wrong[oldToken] ||
          prev.matched[oldToken] ||
          prev.ignored[oldToken];
        if (!entry) return prev;

        const source = prev.unmatched[oldToken]
          ? "unmatched"
          : prev.wrong[oldToken]
            ? "wrong"
            : prev.matched[oldToken]
              ? "matched"
              : "ignored";

        invalidateSignatureCache(oldToken);
        invalidateSignatureCache(newToken);

        const updateSplitFrom = (map: Record<string, TokenEntry>) => {
          const result: Record<string, TokenEntry> = {};
          for (const key of Object.keys(map)) {
            const val = map[key];
            if (val) {
              result[key] =
                val.splitFrom === oldToken
                  ? { ...val, splitFrom: newToken }
                  : val;
            }
          }
          return result;
        };

        let newUnmatched = updateSplitFrom(prev.unmatched);
        let newWrong = updateSplitFrom(prev.wrong);
        let newMatched = updateSplitFrom(prev.matched);
        let newIgnored = updateSplitFrom(prev.ignored);

        const allTokens = [
          ...Object.keys(newMatched),
          ...Object.keys(newUnmatched),
          ...Object.keys(newWrong),
          ...Object.keys(newIgnored),
        ];
        const existingToken = allTokens.find(
          (t) => t.toLowerCase() === newToken.toLowerCase() && t !== oldToken
        );

        if (existingToken) {
          const existingEntry =
            newMatched[existingToken] ||
            newUnmatched[existingToken] ||
            newWrong[existingToken] ||
            newIgnored[existingToken];
          if (existingEntry && doConfirm) {
            const targetField = field ?? existingEntry.field;
            if (newMatched[existingToken]) {
              newMatched[existingToken] = {
                ...existingEntry,
                confirmed: true,
                confirmedField: targetField,
                field: targetField ?? existingEntry.field,
              };
            } else if (newUnmatched[existingToken] && targetField) {
              const moved = {
                ...existingEntry,
                field: targetField,
                confirmed: true,
                confirmedField: targetField,
              };
              delete newUnmatched[existingToken];
              newMatched[existingToken] = moved;
            }
          }
          delete newUnmatched[oldToken];
          delete newWrong[oldToken];
          delete newMatched[oldToken];
          delete newIgnored[oldToken];
          return {
            ...prev,
            unmatched: newUnmatched,
            wrong: newWrong,
            matched: newMatched,
            ignored: newIgnored,
            history: pushHistory(prev, {
              action: "rename",
              token: oldToken,
              newToken,
            }),
          };
        }

        const resolvedField = field ?? entry.field;
        const newEntry = {
          ...entry,
          token: newToken,
          field: resolvedField,
          confirmed: doConfirm ? true : entry.confirmed,
          confirmedField: doConfirm
            ? (resolvedField ?? entry.confirmedField)
            : entry.confirmedField,
        };

        if (source === "unmatched") {
          delete newUnmatched[oldToken];
          if (resolvedField) {
            newMatched[newToken] = newEntry;
          } else {
            newUnmatched[newToken] = newEntry;
          }
        } else if (source === "wrong") {
          delete newWrong[oldToken];
          if (resolvedField) {
            newMatched[newToken] = newEntry;
          } else {
            newUnmatched[newToken] = newEntry;
          }
        } else if (source === "matched") {
          delete newMatched[oldToken];
          newMatched[newToken] = newEntry;
        } else {
          delete newIgnored[oldToken];
          if (resolvedField) {
            newMatched[newToken] = newEntry;
          } else {
            newUnmatched[newToken] = newEntry;
          }
        }

        return {
          ...prev,
          unmatched: newUnmatched,
          wrong: newWrong,
          matched: newMatched,
          ignored: newIgnored,
          history: pushHistory(prev, {
            action: "rename",
            token: oldToken,
            newToken,
          }),
        };
      });
    },
    []
  );

  /**
   * Batch edit: apply context-anchored transformation to all selected tokens.
   */
  const batchEdit = useCallback(
    (oldToken: string, newToken: string, otherTokens: string[]) => {
      // First rename the primary token
      renameToken(oldToken, newToken);

      // Then apply batch edit to others
      for (const otherToken of otherTokens) {
        if (otherToken === oldToken) continue;
        const transformed = applyBatchEdit(otherToken, oldToken, newToken);
        if (transformed && transformed !== otherToken) {
          renameToken(otherToken, transformed);
        }
      }
    },
    [renameToken]
  );

  /**
   * Select similar tokens based on structural similarity.
   * Returns the list of similar tokens (including source).
   */
  const selectSimilar = useCallback(
    (sourceToken: string, threshold?: number): string[] => {
      let result: string[] = [sourceToken];
      const t = threshold ?? state.config.threshold;
      const srcSig = extractTokenSignature(sourceToken);

      // Gather all tokens from all maps
      const allTokens = new Set([
        ...Object.keys(state.matched),
        ...Object.keys(state.unmatched),
        ...Object.keys(state.wrong),
        ...Object.keys(state.ignored),
      ]);

      for (const token of allTokens) {
        if (token === sourceToken) continue;
        const adaptiveSim = calculateAdaptiveSimilarity(srcSig, token);
        const existingSim = calculateStructuralSimilarity(sourceToken, token);
        const similarity = Math.max(adaptiveSim, existingSim);
        if (similarity >= t) {
          result.push(token);
        }
      }

      return result;
    },
    [
      state.matched,
      state.unmatched,
      state.wrong,
      state.ignored,
      state.config.threshold,
    ]
  );

  /**
   * Combine multiple tokens into a single token.
   * Handles mixed sources (matched + unmatched). Result inherits field if all sources share it,
   * otherwise uses the provided field or defaults to null (unmatched).
   */
  const combine = useCallback(
    (tokens: string[], combinedText: string, field?: string | null) => {
      if (tokens.length < 2 || !combinedText) return;

      setState((prev) => {
        // Collect entries and determine source maps
        const entries: Array<{
          token: string;
          entry: TokenEntry;
          source: string;
        }> = [];
        for (const t of tokens) {
          if (prev.matched[t])
            entries.push({
              token: t,
              entry: prev.matched[t]!,
              source: "matched",
            });
          else if (prev.unmatched[t])
            entries.push({
              token: t,
              entry: prev.unmatched[t]!,
              source: "unmatched",
            });
          else if (prev.wrong[t])
            entries.push({ token: t, entry: prev.wrong[t]!, source: "wrong" });
          else if (prev.ignored[t])
            entries.push({
              token: t,
              entry: prev.ignored[t]!,
              source: "ignored",
            });
        }

        if (entries.length < 2) return prev;

        // Determine field for combined token
        let resolvedField: string | null = field ?? null;
        if (resolvedField === undefined) {
          const fields = new Set(entries.map((e) => e.entry.field));
          if (fields.size === 1) resolvedField = entries[0]!.entry.field;
          else resolvedField = null;
        }

        // Merge positions
        const allCols = new Set<number>();
        const allRows = new Set<number>();
        let totalCount = 0;
        for (const e of entries) {
          for (const c of e.entry.positions.cols) allCols.add(c);
          for (const r of e.entry.positions.rows) allRows.add(r);
          totalCount += e.entry.positions.count;
        }

        // Merge origins from source tokens - use the first non-null origin
        let mergedOrigin: TokenOrigin | null = null;
        for (const e of entries) {
          if (e.entry.origin && !mergedOrigin) {
            mergedOrigin = { ...e.entry.origin };
            break;
          }
        }

        const combinedEntry: TokenEntry = {
          token: combinedText,
          field: resolvedField,
          confidence: Math.max(...entries.map((e) => e.entry.confidence)),
          wrong: false,
          positions: {
            cols: Array.from(allCols),
            rows: Array.from(allRows),
            count: totalCount,
          },
          splitFrom: null,
          splitId: null,
          ignored: false,
          confirmed: false,
          confirmedField: null,
          source: entries.find((e) => e.entry.source)?.entry.source || null,
          pattern: null,
          origin: mergedOrigin,
          originConfirmed: {},
        };

        const newMatched = { ...prev.matched };
        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        const newIgnored = { ...prev.ignored };

        // Remove source tokens
        for (const e of entries) {
          delete newMatched[e.token];
          delete newUnmatched[e.token];
          delete newWrong[e.token];
          delete newIgnored[e.token];
        }

        // Add combined token to appropriate map
        if (resolvedField) {
          newMatched[combinedText] = combinedEntry;
        } else {
          newUnmatched[combinedText] = combinedEntry;
        }

        invalidateSignatureCache(combinedText);
        for (const e of entries) invalidateSignatureCache(e.token);

        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          ignored: newIgnored,
          history: pushHistory(prev, {
            action: "combine",
            tokens,
            combinedText,
            field: resolvedField ?? undefined,
          }),
        };
      });
    },
    []
  );

  const groupBySimilarity = useCallback(
    (tokens: string[], threshold?: number) => {
      setState((prev) => {
        const t = threshold ?? prev.config.threshold;
        const sigs = new Map<
          string,
          ReturnType<typeof extractTokenSignature>
        >();
        for (const token of tokens) {
          sigs.set(token, getSignature(token));
        }
        const groups: TokenGroup[] = [];
        const used = new Set<string>();
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i]!;
          if (used.has(token)) continue;
          const group = [token];
          used.add(token);
          for (let j = i + 1; j < tokens.length; j++) {
            const t2 = tokens[j]!;
            if (used.has(t2)) continue;
            const sig1 = sigs.get(token);
            const sig2 = sigs.get(t2);
            if (sig1 && sig2) {
              const sim = calcSimilarity(sig1, sig2, token, t2);
              if (sim >= t) {
                group.push(t2);
                used.add(t2);
              }
            }
          }
          if (group.length > 0) {
            groups.push({
              tokens: group,
              similarity: groupAvgSim(group, sigs),
            });
          }
        }
        groups.sort((a, b) => b.tokens.length - a.tokens.length);
        return { ...prev, groups, config: { ...prev.config, grouped: true } };
      });
    },
    []
  );

  const toggleSelect = useCallback((token: string, checked: boolean) => {
    setSelectedTokens((prev) =>
      checked
        ? [...prev.filter((t) => t !== token), token]
        : prev.filter((t) => t !== token)
    );
  }, []);

  const clearSelection = useCallback(() => setSelectedTokens([]), []);

  const updateThreshold = useCallback((threshold: number) => {
    setState((prev) => ({ ...prev, config: { ...prev.config, threshold } }));
  }, []);

  const switchTab = useCallback(
    (tab: TokenTab) => {
      setState((prev) => ({
        ...prev,
        config: { ...prev.config, currentTab: tab },
      }));
      clearSelection();
    },
    [clearSelection]
  );

  const getResolutions = useCallback((): Array<{
    token: string;
    field: string;
    confirmed: boolean;
    origin?: TokenOrigin | null;
    has_canonical_link?: boolean | null;
    canonical_subject_id?: string | null;
    split?: boolean;
    split_into?: string[];
    auto_split?: boolean;
    override_ignore?: boolean;
  }> => {
    const resolutions: Array<{
      token: string;
      field: string;
      confirmed: boolean;
      origin?: TokenOrigin | null;
      has_canonical_link?: boolean | null;
      canonical_subject_id?: string | null;
      split?: boolean;
      split_into?: string[];
      auto_split?: boolean;
      override_ignore?: boolean;
    }> = [];
    const seen = new Set<string>();

    // Helper to add resolution with proper confirmed status
    const addResolution = (token: string, entry: any) => {
      if (!entry.field || seen.has(token)) return;
      seen.add(token);

      const splitInto = Array.isArray(entry.split_into)
        ? entry.split_into.filter((part: string) => part)
        : [];
      resolutions.push({
        token,
        field: entry.field,
        confirmed: entry.confirmed === true,
        origin: entry.origin || undefined,
        has_canonical_link: entry.has_canonical_link ?? null,
        canonical_subject_id: entry.canonical_subject_id ?? null,
        override_ignore: entry.overrideIgnore === true,
        split: Boolean(entry.is_split || splitInto.length >= 2) || undefined,
        split_into: splitInto.length >= 2 ? splitInto : undefined,
        auto_split: Boolean(entry.auto_split) || undefined,
      });
    };

    for (const [token, entry] of Object.entries(state.matched)) {
      // Skip tokens with field='split' - they are handled via state.splits
      if (entry.field === "split") continue;
      addResolution(token, entry);
    }
    for (const [token, entry] of Object.entries(state.wrong)) {
      addResolution(token, entry);
    }
    for (const [token, entry] of Object.entries(state.unmatched)) {
      addResolution(token, entry);
    }
    for (const [token, entry] of Object.entries(state.ignored)) {
      addResolution(token, entry);
    }

    // CRITICAL: Add split info for tokens that were split
    // This tells the backend to mark these tokens as "split" so they won't appear again
    for (const [originalToken, parts] of Object.entries(state.splits)) {
      if (!parts || parts.length < 2) continue;
      const existing = resolutions.find(
        (item) => item.token.toLowerCase() === originalToken.toLowerCase()
      );
      if (existing) {
        existing.split = true;
        existing.split_into = parts;
        existing.auto_split =
          Boolean(existing.auto_split) ||
          autoSplitRef.current.has(originalToken);
        continue;
      }
      if (seen.has(originalToken)) continue;
      seen.add(originalToken);
      resolutions.push({
        token: originalToken,
        field: "split",
        confirmed: true,
        split: true,
        split_into: parts,
        auto_split: autoSplitRef.current.has(originalToken),
      });
    }

    // Debug: log split tokens
    const splitTokens = resolutions.filter((r) => r.split);
    const confirmedResolutions = resolutions.filter((r) => r.confirmed);
    if (import.meta.env.DEV) {
      console.log("[useTokenStore] getResolutions:", {
        total: resolutions.length,
        confirmed: confirmedResolutions.length,
        splitTokens: splitTokens.map((r) => ({
          token: r.token,
          split_into: r.split_into,
        })),
        sampleConfirmed: confirmedResolutions
          .slice(0, 5)
          .map((r) => ({ token: r.token, field: r.field })),
      });
    }

    return resolutions;
  }, [
    state.matched,
    state.wrong,
    state.unmatched,
    state.ignored,
    state.splits,
  ]);

  const isConfirmed = useCallback(
    (token: string): boolean => {
      return (
        state.matched[token]?.confirmed === true ||
        state.wrong[token]?.confirmed === true ||
        state.ignored[token]?.confirmed === true
      );
    },
    [state.matched, state.wrong, state.ignored]
  );

  const unconfirmToken = useCallback((token: string) => {
    setState((prev) => {
      const updateEntry = (entry: TokenEntry | undefined) => {
        if (!entry) return entry;
        return { ...entry, confirmed: false, confirmedField: null };
      };
      const newMatched = { ...prev.matched };
      const newWrong = { ...prev.wrong };
      const newIgnored = { ...prev.ignored };
      const updatedMatched = updateEntry(newMatched[token]);
      const updatedWrong = updateEntry(newWrong[token]);
      const updatedIgnored = updateEntry(newIgnored[token]);
      if (updatedMatched) newMatched[token] = updatedMatched;
      if (updatedWrong) newWrong[token] = updatedWrong;
      if (updatedIgnored) newIgnored[token] = updatedIgnored;
      return {
        ...prev,
        matched: newMatched,
        wrong: newWrong,
        ignored: newIgnored,
      };
    });
  }, []);

  const confirmToken = useCallback(
    (token: string): { extracted: number; modified: string[] } => {
      const result = { extracted: 0, modified: [] as string[] };
      setState((prev) => {
        const entry = prev.matched[token];
        if (!entry || !entry.field) return prev;

        const newMatched = { ...prev.matched };
        newMatched[token] = {
          ...entry,
          confirmed: true,
          confirmedField: entry.field,
          originConfirmed: entry.origin
            ? {
                ...(entry.originConfirmed || {}),
                [getOriginKey(entry.origin)]: true,
              }
            : entry.originConfirmed,
        };

        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        const newIgnored = { ...prev.ignored };

        const allMaps = [
          { map: newUnmatched, name: "unmatched" },
          { map: newWrong, name: "wrong" },
          { map: newIgnored, name: "ignored" },
        ];

        const processedCompounds = new Set<string>();
        let changed = true;
        let depth = 0;
        const maxDepth = 10;

        while (changed && depth < maxDepth) {
          changed = false;
          depth++;

          for (const confirmedTok of Object.keys(newMatched)) {
            const confirmedEntry = newMatched[confirmedTok];
            if (
              !confirmedEntry ||
              !confirmedEntry.field ||
              !confirmedEntry.confirmed
            )
              continue;

            for (const { map } of allMaps) {
              for (const compoundTok of Object.keys(map)) {
                if (processedCompounds.has(compoundTok)) continue;
                if (compoundTok.toLowerCase() === confirmedTok.toLowerCase())
                  continue;
                if (
                  !compoundTok
                    .toLowerCase()
                    .includes(confirmedTok.toLowerCase())
                )
                  continue;

                const remaining = splitConfirmedFromCompound(
                  confirmedTok,
                  compoundTok
                );
                if (!remaining) continue;

                processedCompounds.add(compoundTok);
                delete map[compoundTok];
                result.extracted++;
                result.modified.push(compoundTok);

                const firstPart = remaining[0];
                if (
                  firstPart &&
                  remaining.length === 1 &&
                  !newMatched[firstPart] &&
                  !newUnmatched[firstPart] &&
                  !newWrong[firstPart] &&
                  !newIgnored[firstPart]
                ) {
                  const sourceEntry =
                    prev.matched[compoundTok] ||
                    prev.wrong[compoundTok] ||
                    prev.ignored[compoundTok];
                  const positions = sourceEntry?.positions || {
                    cols: [],
                    rows: [],
                    count: 0,
                  };
                  const parentOrigin = sourceEntry?.origin;
                  newUnmatched[firstPart] = {
                    token: firstPart,
                    field: null,
                    confidence: 0,
                    wrong: false,
                    positions,
                    splitFrom: compoundTok,
                    splitId: `confirm_${Date.now()}`,
                    ignored: false,
                    confirmed: false,
                    confirmedField: null,
                    source: sourceEntry?.source || null,
                    pattern: sourceEntry?.pattern || null,
                    origin: parentOrigin
                      ? {
                          ...parentOrigin,
                          structural: {
                            is_part_of_compound: true,
                            compound_parent: compoundTok,
                            related_subject:
                              parentOrigin.structural?.related_subject,
                            related_subject_confirmed:
                              parentOrigin.structural
                                ?.related_subject_confirmed ?? false,
                            appears_in_columns:
                              parentOrigin.structural?.appears_in_columns ?? [],
                            appears_in_rows:
                              parentOrigin.structural?.appears_in_rows ?? [],
                            co_occurs_with:
                              parentOrigin.structural?.co_occurs_with ?? {},
                          },
                        }
                      : null,
                    originConfirmed: {},
                  };
                  result.modified.push(firstPart);
                  changed = true;
                } else if (remaining.length > 1) {
                  for (const part of remaining) {
                    if (
                      part &&
                      !newMatched[part] &&
                      !newUnmatched[part] &&
                      !newWrong[part] &&
                      !newIgnored[part]
                    ) {
                      const sourceEntry =
                        prev.matched[compoundTok] ||
                        prev.wrong[compoundTok] ||
                        prev.ignored[compoundTok];
                      const positions = sourceEntry?.positions || {
                        cols: [],
                        rows: [],
                        count: 0,
                      };
                      const parentOrigin = sourceEntry?.origin;
                      newUnmatched[part] = {
                        token: part,
                        field: null,
                        confidence: 0,
                        wrong: false,
                        positions,
                        splitFrom: compoundTok,
                        splitId: `confirm_${Date.now()}`,
                        ignored: false,
                        confirmed: false,
                        confirmedField: null,
                        source: sourceEntry?.source || null,
                        pattern: sourceEntry?.pattern || null,
                        origin: parentOrigin
                          ? {
                              ...parentOrigin,
                              structural: {
                                is_part_of_compound: true,
                                compound_parent: compoundTok,
                                related_subject:
                                  parentOrigin.structural?.related_subject,
                                related_subject_confirmed:
                                  parentOrigin.structural
                                    ?.related_subject_confirmed ?? false,
                                appears_in_columns:
                                  parentOrigin.structural?.appears_in_columns ??
                                  [],
                                appears_in_rows:
                                  parentOrigin.structural?.appears_in_rows ??
                                  [],
                                co_occurs_with:
                                  parentOrigin.structural?.co_occurs_with ?? {},
                              },
                            }
                          : null,
                        originConfirmed: {},
                      };
                      result.modified.push(part);
                      changed = true;
                    }
                  }
                }

                if (!newMatched[confirmedTok]) {
                  newMatched[confirmedTok] = {
                    ...confirmedEntry,
                    token: confirmedTok,
                  };
                }
              }
            }
          }
        }

        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          ignored: newIgnored,
        };
      });
      return result;
    },
    []
  );

  const confirmTokens = useCallback(
    (tokens: string[]): { extracted: number; modified: string[] } => {
      const result = { extracted: 0, modified: [] as string[] };
      console.log("[useTokenStore] confirmTokens called with:", tokens);
      setState((prev) => {
        const tokensToConfirm = tokens.filter(
          (t) => prev.matched[t] && prev.matched[t]?.field
        );
        if (tokensToConfirm.length === 0) {
          console.log(
            "[useTokenStore] confirmTokens: no tokens to confirm (no matched with field)"
          );
          return prev;
        }
        console.log(
          "[useTokenStore] confirmTokens: confirming",
          tokensToConfirm.length,
          "tokens"
        );

        const newMatched = { ...prev.matched };
        for (const t of tokensToConfirm) {
          const entry = newMatched[t]!;
          newMatched[t] = {
            ...entry,
            confirmed: true,
            confirmedField: entry.field,
            originConfirmed: entry.origin
              ? {
                  ...(entry.originConfirmed || {}),
                  [getOriginKey(entry.origin)]: true,
                }
              : entry.originConfirmed,
          };
          // Debug: log split token confirmation
          if (entry.splitFrom) {
            console.log(
              "[useTokenStore] confirmTokens: confirmed split token",
              { token: t, splitFrom: entry.splitFrom, field: entry.field }
            );
          }
        }

        const newUnmatched = { ...prev.unmatched };
        const newWrong = { ...prev.wrong };
        const newIgnored = { ...prev.ignored };

        const allMaps = [
          { map: newUnmatched },
          { map: newWrong },
          { map: newIgnored },
        ];

        const processedCompounds = new Set<string>();
        let changed = true;
        let depth = 0;
        const maxDepth = 10;

        while (changed && depth < maxDepth) {
          changed = false;
          depth++;

          for (const confirmedTok of Object.keys(newMatched)) {
            const confirmedEntry = newMatched[confirmedTok];
            if (
              !confirmedEntry ||
              !confirmedEntry.field ||
              !confirmedEntry.confirmed
            )
              continue;

            for (const { map } of allMaps) {
              for (const compoundTok of Object.keys(map)) {
                if (processedCompounds.has(compoundTok)) continue;
                if (compoundTok.toLowerCase() === confirmedTok.toLowerCase())
                  continue;
                if (
                  !compoundTok
                    .toLowerCase()
                    .includes(confirmedTok.toLowerCase())
                )
                  continue;

                const remaining = splitConfirmedFromCompound(
                  confirmedTok,
                  compoundTok
                );
                if (!remaining) continue;

                processedCompounds.add(compoundTok);
                delete map[compoundTok];
                result.extracted++;
                result.modified.push(compoundTok);

                for (const part of remaining) {
                  if (
                    part &&
                    !newMatched[part] &&
                    !newUnmatched[part] &&
                    !newWrong[part] &&
                    !newIgnored[part]
                  ) {
                    const sourceEntry =
                      prev.matched[compoundTok] ||
                      prev.wrong[compoundTok] ||
                      prev.ignored[compoundTok];
                    const positions = sourceEntry?.positions || {
                      cols: [],
                      rows: [],
                      count: 0,
                    };
                    const parentOrigin = sourceEntry?.origin;
                    newUnmatched[part] = {
                      token: part,
                      field: null,
                      confidence: 0,
                      wrong: false,
                      positions,
                      splitFrom: compoundTok,
                      splitId: `confirm_${Date.now()}`,
                      ignored: false,
                      confirmed: false,
                      confirmedField: null,
                      source: sourceEntry?.source || null,
                      pattern: sourceEntry?.pattern || null,
                      origin: parentOrigin
                        ? {
                            ...parentOrigin,
                            structural: {
                              is_part_of_compound: true,
                              compound_parent: compoundTok,
                              related_subject:
                                parentOrigin.structural?.related_subject,
                              related_subject_confirmed:
                                parentOrigin.structural
                                  ?.related_subject_confirmed ?? false,
                              appears_in_columns:
                                parentOrigin.structural?.appears_in_columns ??
                                [],
                              appears_in_rows:
                                parentOrigin.structural?.appears_in_rows ?? [],
                              co_occurs_with:
                                parentOrigin.structural?.co_occurs_with ?? {},
                            },
                          }
                        : null,
                      originConfirmed: {},
                    };
                    result.modified.push(part);
                    changed = true;
                  }
                }

                if (!newMatched[confirmedTok]) {
                  newMatched[confirmedTok] = {
                    ...confirmedEntry,
                    token: confirmedTok,
                  };
                }
              }
            }
          }
        }

        return {
          ...prev,
          matched: newMatched,
          unmatched: newUnmatched,
          wrong: newWrong,
          ignored: newIgnored,
        };
      });
      return result;
    },
    []
  );

  return {
    state,
    selectedTokensList: selectedTokens,
    toggleSelect,
    clearSelection,
    assign,
    assignAndConfirm,
    assignAndConfirmMany,
    unassign,
    markWrong,
    unmarkWrong,
    ignore,
    batchIgnore,
    unignore,
    split,
    batchSplit,
    splitMany,
    applyAssignedSplits,
    batchEdit,
    selectSimilar,
    renameToken,
    renameAndConfirm,
    merge,
    combine,
    groupBySimilarity,
    updateThreshold,
    switchTab,
    getResolutions,
    confirmToken,
    confirmTokens,
    unconfirmToken,
    isConfirmed,
    combineWithSimilar: useCallback(
      (
        sourceTokens: string[],
        similarSuffixes: string[],
        field?: string | null
      ): { created: number; skipped: number } => {
        const result = { created: 0, skipped: 0 };
        if (sourceTokens.length < 2) return result;

        setState((prev) => {
          const prefix = sourceTokens[0]!;
          const initialSuffix = sourceTokens[1]!;
          const prefixEntry =
            prev.matched[prefix] ||
            prev.unmatched[prefix] ||
            prev.wrong[prefix] ||
            prev.ignored[prefix];
          if (!prefixEntry) return prev;

          const resolvedField = field ?? prefixEntry.field;

          // Collect ALL suffixes: initial + similar, deduplicated
          const allSuffixes = [
            initialSuffix,
            ...similarSuffixes.filter(
              (s) => s.toLowerCase() !== initialSuffix.toLowerCase()
            ),
          ];

          const newMatched = { ...prev.matched };
          const newUnmatched = { ...prev.unmatched };
          const newWrong = { ...prev.wrong };
          const newIgnored = { ...prev.ignored };

          // Remove ALL source tokens (prefix + all suffixes)
          delete newMatched[prefix];
          delete newUnmatched[prefix];
          delete newWrong[prefix];
          delete newIgnored[prefix];
          for (const suffix of allSuffixes) {
            delete newMatched[suffix];
            delete newUnmatched[suffix];
            delete newWrong[suffix];
            delete newIgnored[suffix];
          }

          // Create combined tokens for each suffix
          for (const suffix of allSuffixes) {
            const suffixEntry =
              prev.matched[suffix] ||
              prev.unmatched[suffix] ||
              prev.wrong[suffix] ||
              prev.ignored[suffix];
            const combinedText = `${prefix} ${suffix}`;

            // Skip if combined token already exists
            if (
              newMatched[combinedText] ||
              newUnmatched[combinedText] ||
              newWrong[combinedText] ||
              newIgnored[combinedText]
            ) {
              result.skipped++;
              continue;
            }

            const suffixPositions = suffixEntry?.positions || {
              cols: [],
              rows: [],
              count: 0,
            };
            const mergedCols = Array.from(
              new Set([...prefixEntry.positions.cols, ...suffixPositions.cols])
            );
            const mergedRows = Array.from(
              new Set([...prefixEntry.positions.rows, ...suffixPositions.rows])
            );
            const mergedCount =
              prefixEntry.positions.count + suffixPositions.count;

            const combinedEntry: TokenEntry = {
              token: combinedText,
              field: resolvedField,
              confidence: Math.max(
                prefixEntry.confidence,
                suffixEntry?.confidence || 0
              ),
              wrong: false,
              positions: {
                cols: mergedCols,
                rows: mergedRows,
                count: mergedCount,
              },
              splitFrom: null,
              splitId: `combine_similar_${Date.now()}`,
              ignored: false,
              confirmed: false,
              confirmedField: null,
            };

            if (resolvedField) {
              newMatched[combinedText] = combinedEntry;
            } else {
              newUnmatched[combinedText] = combinedEntry;
            }
            result.created++;
            invalidateSignatureCache(combinedText);
          }

          invalidateSignatureCache(prefix);
          for (const suffix of allSuffixes) invalidateSignatureCache(suffix);

          return {
            ...prev,
            matched: newMatched,
            unmatched: newUnmatched,
            wrong: newWrong,
            ignored: newIgnored,
          };
        });
        return result;
      },
      []
    ),
    suggestField: (token: string) =>
      suggestField(token, state.config.orphanPatterns),
    reset: useCallback(() => {
      setState(
        buildInitialState(
          initialMatched,
          initialUnmatched,
          orphanPatterns,
          tokenPositions,
          splitTracking
        )
      );
    }, [
      initialMatched,
      initialUnmatched,
      orphanPatterns,
      tokenPositions,
      splitTracking,
    ]),
    undo: useCallback((): TokenUndoResult | null => {
      const prev = stateRef.current;
      if (prev.history.length === 0) return null;
      const lastAction = prev.history[prev.history.length - 1]!;
      const snapshot = lastAction.snapshot;
      const tokens = [
        ...(lastAction.token ? [lastAction.token] : []),
        ...(lastAction.tokens || []),
        ...(lastAction.sources || []),
        ...(lastAction.parts || []),
      ].filter((token, index, all) => all.indexOf(token) === index);
      const first = tokens[0];
      const bucket =
        snapshot && first
          ? snapshotBucket(snapshot, first) || "unmatched"
          : "unmatched";
      setState((current) => {
        if (current.history.length === 0) return current;
        const action = current.history[current.history.length - 1]!;
        if (!action.snapshot)
          return { ...current, history: current.history.slice(0, -1) };
        return {
          ...current,
          matched: action.snapshot.matched,
          unmatched: action.snapshot.unmatched,
          wrong: action.snapshot.wrong,
          ignored: action.snapshot.ignored,
          splits: action.snapshot.splits,
          history: current.history.slice(0, -1),
        };
      });
      return { tokens, bucket, action: lastAction.action };
    }, []),
  };
}
